import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { libraryAPI } from '../../utils/api';

const DOC_TYPES = [
  { value: 'constitution', label: 'Constitution' },
  { value: 'act', label: 'Act' },
  { value: 'si', label: 'Statutory Instrument' },
  { value: 'regulation', label: 'Regulation' },
  { value: 'rules', label: 'Rules of Court' },
  { value: 'case', label: 'Case' },
  { value: 'other', label: 'Other' },
];

const DOC_TYPE_LABEL = DOC_TYPES.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

const EMPTY_FORM = {
  title: '',
  doc_type: 'act',
  citation: '',
  jurisdiction: '',
  source_url: '',
  attribution: '',
  frbr_uri: '',
  publish: true,
};

const inputClass =
  'mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white';

const LibraryDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [formError, setFormError] = useState('');

  const loadDocuments = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      const res = await libraryAPI.getDocuments();
      const data = res.data?.results || res.data || [];
      setDocuments(Array.isArray(data) ? data : []);
    } catch (err) {
      setListError('Could not load documents.');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setSuccess('');
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess('');
    setFormError('');

    if (!file) {
      setFormError('Please choose a file to upload.');
      return;
    }
    if (!form.title.trim()) {
      setFormError('Please enter a title.');
      return;
    }

    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', form.title.trim());
    fd.append('doc_type', form.doc_type);
    if (form.citation) fd.append('citation', form.citation);
    if (form.jurisdiction) fd.append('jurisdiction', form.jurisdiction);
    if (form.source_url) fd.append('source_url', form.source_url);
    if (form.attribution) fd.append('attribution', form.attribution);
    if (form.frbr_uri) fd.append('frbr_uri', form.frbr_uri);
    fd.append('publish', form.publish ? 'true' : 'false');

    setSubmitting(true);
    try {
      const res = await libraryAPI.uploadDocument(fd);
      setSuccess(`Uploaded "${res.data?.title || form.title}".`);
      setForm(EMPTY_FORM);
      setFile(null);
      // Reset the native file input.
      e.target.reset();
      await loadDocuments();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (err?.response?.status === 403
          ? 'You do not have permission to upload documents.'
          : null) ||
        'Upload failed. Please check the file and try again.';
      setFormError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Legal Library</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Upload and manage hosted primary legal sources available to all users.
        </p>
      </div>

      {/* Upload form */}
      <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/10 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Upload document</h3>

        {success && (
          <div className="mb-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 p-3 rounded-md text-sm">
            {success}
          </div>
        )}
        {formError && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 p-3 rounded-md text-sm">
            {formError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 max-w-2xl">
          <div>
            <label htmlFor="file" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              File
            </label>
            <input
              type="file"
              id="file"
              name="file"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                setSuccess('');
                setFormError('');
              }}
              className="mt-1 block w-full text-sm text-gray-600 dark:text-gray-300 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-primary-900/30 dark:file:text-primary-300"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Title
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={form.title}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="doc_type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Document type
              </label>
              <select
                id="doc_type"
                name="doc_type"
                value={form.doc_type}
                onChange={handleChange}
                className={inputClass}
              >
                {DOC_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="jurisdiction" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Jurisdiction <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                id="jurisdiction"
                name="jurisdiction"
                value={form.jurisdiction}
                onChange={handleChange}
                placeholder="Zimbabwe"
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="citation" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Citation <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                id="citation"
                name="citation"
                value={form.citation}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="frbr_uri" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                FRBR URI <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                id="frbr_uri"
                name="frbr_uri"
                value={form.frbr_uri}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="source_url" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Source URL <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="url"
                id="source_url"
                name="source_url"
                value={form.source_url}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="attribution" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Attribution <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                id="attribution"
                name="attribution"
                value={form.attribution}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="publish"
              name="publish"
              checked={form.publish}
              onChange={handleChange}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="publish" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Publish now (visible to all users)
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex justify-center items-center px-5 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {submitting ? 'Uploading…' : 'Upload document'}
          </button>
        </form>
      </div>

      {/* Existing documents */}
      <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/10 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Existing documents</h3>

        {listError && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 p-3 rounded-md text-sm">
            {listError}
          </div>
        )}

        {listLoading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500" />
          </div>
        ) : documents.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No documents yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Citation</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {documents.map((doc) => (
                  <tr key={doc.id}>
                    <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{doc.title}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                      {DOC_TYPE_LABEL[doc.doc_type] || doc.doc_type}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">{doc.citation || '—'}</td>
                    <td className="px-3 py-2 text-sm">
                      {doc.is_published ? (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Published</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">Draft</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-right">
                      <Link to={`/library/${doc.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LibraryDocuments;
